import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, ZoomIn, ZoomOut, X, Check } from 'lucide-react';

interface SponsorLogoCropperProps {
  onCropComplete: (blob: Blob) => void;
  onCancel: () => void;
}

// 3:1 landscape ratio — standard sponsor banner
const PREVIEW_W = 360;
const PREVIEW_H = 120;
const OUTPUT_W = 600;
const OUTPUT_H = 200;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;

const SponsorLogoCropper: React.FC<SponsorLogoCropperProps> = ({ onCropComplete, onCancel }) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = PREVIEW_W;
    canvas.height = PREVIEW_H;

    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, PREVIEW_W, PREVIEW_H);

    ctx.save();
    ctx.translate(PREVIEW_W / 2 + offset.x, PREVIEW_H / 2 + offset.y);
    ctx.scale(zoom, zoom);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);
    ctx.restore();

    // Overlay border
    ctx.strokeStyle = 'rgba(249,115,22,0.8)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, PREVIEW_W - 2, PREVIEW_H - 2);

    // Rule-of-thirds guides
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(i * PREVIEW_W / 3, 0);
      ctx.lineTo(i * PREVIEW_W / 3, PREVIEW_H);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(0, PREVIEW_H / 2);
    ctx.lineTo(PREVIEW_W, PREVIEW_H / 2);
    ctx.stroke();
  }, [image, zoom, offset]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target?.result as string;
      const img = new Image();
      img.onload = () => {
        // SVGs without intrinsic width/height report 0 — fall back to sensible defaults
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (!w || !h) {
          w = OUTPUT_W;
          h = OUTPUT_H;
          img.width = w;
          img.height = h;
        }
        const scale = Math.max(PREVIEW_W / w, PREVIEW_H / h);
        setZoom(scale);
        setOffset({ x: 0, y: 0 });
        setImage(img);
      };
      img.onerror = () => {
        console.error('Failed to load image');
      };
      img.src = src;
      if (isSvg) {
        img.width = OUTPUT_W;
        img.height = OUTPUT_H;
      }
    };
    reader.readAsDataURL(file);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setDragging(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    setDragging(true);
    setDragStart({ x: t.clientX - offset.x, y: t.clientY - offset.y });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return;
    const t = e.touches[0];
    setOffset({ x: t.clientX - dragStart.x, y: t.clientY - dragStart.y });
  };

  const handleSave = () => {
    if (!image) return;
    setSaving(true);
    const out = document.createElement('canvas');
    out.width = OUTPUT_W;
    out.height = OUTPUT_H;
    const ctx = out.getContext('2d')!;
    ctx.fillStyle = 'transparent';
    ctx.clearRect(0, 0, OUTPUT_W, OUTPUT_H);

    const scaleRatio = OUTPUT_W / PREVIEW_W;
    ctx.save();
    ctx.translate(OUTPUT_W / 2 + offset.x * scaleRatio, OUTPUT_H / 2 + offset.y * scaleRatio);
    ctx.scale(zoom * scaleRatio, zoom * scaleRatio);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);
    ctx.restore();

    out.toBlob((blob) => {
      if (blob) onCropComplete(blob);
      setSaving(false);
    }, 'image/png');
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl p-6 w-full max-w-lg border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg">Crop Sponsor Logo</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <p className="text-gray-400 text-sm mb-4">
          Drag to reposition. Adjust zoom for best fit in the 3:1 banner area.
        </p>

        {!image ? (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-600 rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 transition-colors"
          >
            <Upload size={28} className="text-gray-500 mb-2" />
            <p className="text-gray-400 text-sm">Click to upload logo</p>
            <p className="text-gray-600 text-xs mt-1">PNG, JPG, SVG — max 5MB</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className="rounded-lg overflow-hidden cursor-grab active:cursor-grabbing mx-auto"
              style={{ width: PREVIEW_W, height: PREVIEW_H }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleMouseUp}
            >
              <canvas ref={canvasRef} style={{ display: 'block' }} />
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - 0.1))}
                className="p-2 bg-gray-800 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
              >
                <ZoomOut size={16} />
              </button>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.05}
                value={zoom}
                onChange={e => setZoom(parseFloat(e.target.value))}
                className="flex-1 accent-orange-500"
              />
              <button
                onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + 0.1))}
                className="p-2 bg-gray-800 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
              >
                <ZoomIn size={16} />
              </button>
            </div>

            <button
              onClick={() => { setImage(null); setOffset({ x: 0, y: 0 }); setZoom(1); }}
              className="text-sm text-gray-400 hover:text-white transition-colors underline"
            >
              Choose different image
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml,.svg"
          className="hidden"
          onChange={handleFileChange}
        />

        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            className="flex-1 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!image || saving}
            className="flex-1 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Check size={16} />
            )}
            {saving ? 'Saving...' : 'Use Logo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SponsorLogoCropper;
